import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  type Attempt,
  type SessionResult,
  SessionRecorder,
  recordAttempt,
  pickWeightedKey,
  statsFor,
} from "@nav-games/games-core";
import {
  IALA_IDS,
  IALA_MARKS,
  REGIONS,
  lightForRegion,
  regionLabel,
  type Region,
} from "@nav-games/iala";

import { BuoyFigure } from "../../ui/BuoyFigure";
import { HelpButton, HelpPanel } from "../../ui/HelpPanel";
import { LightRhythm } from "../../ui/LightRhythm";

interface FlashcardsProps {
  onNavigate: (to: string) => void;
}

const MODULE = "iala-buoyage";
const GAME = "flashcards";
const CARDS_PER_SESSION = 12;
const BEAT_THE_CLOCK_MS = 90_000;
const CHOICES_PER_CARD = 4;

// Recent answers kept out of the next draw so the spaced-repetition
// picker doesn't resurface the same card within a short window.
// Clamped to pool size - 1 so there is always an eligible card.
const AVOID_WINDOW = Math.min(8, IALA_IDS.length - 1);

type SessionMode = "cards" | "timed";

interface Card {
  answer: string;
  region: Region;
  choices: readonly string[];
}

function nextCard(fixedRegion: Region | null, recent: readonly string[]): Card {
  const answer = pickWeightedKey(MODULE, IALA_IDS, recent);
  // For mixed mode, pick region at random per card so the lateral
  // marks get drilled both ways. For region-fixed mode the picker
  // forces one side, which is more useful when learning from cold.
  const region =
    fixedRegion ?? (Math.random() < 0.5 ? "A" : "B");
  const distractors = new Set<string>();
  while (distractors.size < CHOICES_PER_CARD - 1) {
    const d = IALA_IDS[Math.floor(Math.random() * IALA_IDS.length)]!;
    if (d !== answer) distractors.add(d);
  }
  const choices = shuffle([answer, ...distractors]);
  return { answer, region, choices };
}

function shuffle<T>(input: readonly T[]): T[] {
  const arr = [...input];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const ai = arr[i]!;
    const aj = arr[j]!;
    arr[i] = aj;
    arr[j] = ai;
  }
  return arr;
}

export function Flashcards({ onNavigate }: FlashcardsProps) {
  const [sessionMode, setSessionMode] = useState<SessionMode | null>(null);
  // Region selection: "A", "B", or null = mixed.
  const [regionMode, setRegionMode] = useState<Region | "mixed" | null>(null);

  if (!sessionMode || !regionMode) {
    return (
      <Picker
        onStart={(s, r) => {
          setSessionMode(s);
          setRegionMode(r);
        }}
        onBack={() => onNavigate("/play/buoyage")}
      />
    );
  }
  return (
    <Game
      sessionMode={sessionMode}
      regionMode={regionMode}
      onAgain={() => {
        /* keep current settings */
      }}
      onBack={() => onNavigate("/play/buoyage")}
    />
  );
}

function Picker({
  onStart,
  onBack,
}: {
  onStart: (s: SessionMode, r: Region | "mixed") => void;
  onBack: () => void;
}) {
  return (
    <>
      <h2>Identify the buoy</h2>
      <p className="muted">
        Pick a region (or "mixed" to drill both) and a session length.
      </p>
      {REGIONS.map((r) => (
        <div key={r} className="card">
          <h3>Region {r}</h3>
          <p className="small muted" style={{ marginBottom: 8 }}>
            {regionLabel(r)}
          </p>
          <div
            className="row"
            style={{ flexDirection: "column", gap: 8, alignItems: "stretch" }}
          >
            <button onClick={() => onStart("cards", r)}>
              {CARDS_PER_SESSION} cards
            </button>
            <button
              className="secondary"
              onClick={() => onStart("timed", r)}
            >
              Beat the clock — {Math.round(BEAT_THE_CLOCK_MS / 1000)} s
            </button>
          </div>
        </div>
      ))}
      <div className="card">
        <h3>Mixed</h3>
        <p className="small muted" style={{ marginBottom: 8 }}>
          Cards alternate between Region A and Region B at random — the
          best test once you've learned each region individually.
        </p>
        <div
          className="row"
          style={{ flexDirection: "column", gap: 8, alignItems: "stretch" }}
        >
          <button onClick={() => onStart("cards", "mixed")}>
            {CARDS_PER_SESSION} cards
          </button>
          <button
            className="secondary"
            onClick={() => onStart("timed", "mixed")}
          >
            Beat the clock — {Math.round(BEAT_THE_CLOCK_MS / 1000)} s
          </button>
        </div>
      </div>
      <button className="secondary" onClick={onBack}>
        ← Back to Buoyage
      </button>
    </>
  );
}

interface GameProps {
  sessionMode: SessionMode;
  regionMode: Region | "mixed";
  onAgain: () => void;
  onBack: () => void;
}

function Game({ sessionMode, regionMode, onBack }: GameProps) {
  const recorder = useMemo(
    () =>
      new SessionRecorder({
        module: MODULE,
        game: GAME,
        length: sessionMode === "cards" ? CARDS_PER_SESSION : 0,
      }),
    [sessionMode],
  );

  const fixedRegion = regionMode === "mixed" ? null : regionMode;
  const recentRef = useRef<string[]>([]);
  const [index, setIndex] = useState(0);
  const [card, setCard] = useState<Card>(() => {
    const c = nextCard(fixedRegion, []);
    recentRef.current = [c.answer];
    return c;
  });
  const [shownAt, setShownAt] = useState<number>(() => Date.now());
  const [feedback, setFeedback] = useState<"right" | "wrong" | null>(null);
  const [result, setResult] = useState<SessionResult | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const sessionStartedAt = useRef<number>(Date.now());

  const [now, setNow] = useState<number>(Date.now());
  useEffect(() => {
    if (sessionMode !== "timed" || result) return;
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, [sessionMode, result]);
  const timeLeftMs = Math.max(
    0,
    BEAT_THE_CLOCK_MS - (now - sessionStartedAt.current),
  );
  useEffect(() => {
    if (sessionMode !== "timed" || result) return;
    if (timeLeftMs === 0) setResult(recorder.summarise());
  }, [timeLeftMs, sessionMode, result, recorder]);

  const advance = useCallback(() => {
    if (sessionMode === "cards" && index + 1 >= CARDS_PER_SESSION) {
      setResult(recorder.summarise());
      return;
    }
    setIndex((i) => i + 1);
    const next = nextCard(fixedRegion, recentRef.current);
    recentRef.current = [...recentRef.current, next.answer].slice(-AVOID_WINDOW);
    setCard(next);
    setShownAt(Date.now());
    setFeedback(null);
  }, [index, recorder, sessionMode, fixedRegion]);

  const onChoose = useCallback(
    (choice: string) => {
      if (feedback || result) return;
      const ms = Date.now() - shownAt;
      const ok = choice === card.answer;
      const attempt: Omit<Attempt, "module"> = {
        char: card.answer,
        ok,
        ms,
        ts: Date.now(),
      };
      recorder.log(attempt);
      recordAttempt({ ...attempt, module: MODULE });
      setFeedback(ok ? "right" : "wrong");
      setTimeout(advance, ok ? 1500 : 3500);
    },
    [advance, card, feedback, recorder, result, shownAt],
  );

  if (result) {
    return <EndCard result={result} onBack={onBack} />;
  }

  const liveCorrect = recorder.summarise().correct;
  const header =
    sessionMode === "cards"
      ? `Card ${index + 1} of ${CARDS_PER_SESSION}`
      : `${Math.ceil(timeLeftMs / 1000)} s left · ${liveCorrect} right`;

  const mark = IALA_MARKS[card.answer]!;
  const regionPill =
    regionMode === "mixed" ? `Region ${card.region}` : null;

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <HelpButton onOpen={() => setHelpOpen(true)} />
      </div>
      <HelpPanel section={helpOpen ? "buoyage" : null} onClose={() => setHelpOpen(false)} />
      <p className="small muted">
        {header}
        {regionPill && (
          <>
            {" · "}
            <span className="pill">{regionPill}</span>
          </>
        )}
      </p>
      <div className="card" style={{ textAlign: "center" }}>
        <BuoyFigure mark={mark} region={card.region} size={200} />
        {feedback && (
          <div
            style={{
              marginTop: 8,
              padding: 8,
              borderRadius: 6,
              background:
                feedback === "right"
                  ? "rgba(44, 122, 59, 0.10)"
                  : "rgba(184, 48, 42, 0.10)",
              textAlign: "left",
            }}
          >
            <p style={{ margin: 0, fontWeight: 600 }}>
              {feedback === "right" ? "✓ " : "✗ "}
              {mark.name}
            </p>
            <p className="small muted" style={{ margin: "4px 0 0" }}>
              {mark.meaning}
            </p>
            {(() => {
              const light = lightForRegion(mark, card.region);
              return (
                <p
                  className="small muted"
                  style={{
                    margin: "4px 0 0",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <LightRhythm mark={mark} region={card.region} size={14} />
                  <span>
                    <strong>Light:</strong> {light.description}{" "}
                    <em>({light.abbreviation})</em>
                  </span>
                </p>
              );
            })()}
          </div>
        )}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 8,
            marginTop: 16,
          }}
        >
          {card.choices.map((id) => (
            <button
              key={id}
              className="secondary"
              disabled={!!feedback}
              onClick={() => onChoose(id)}
              style={{
                fontSize: 13,
                padding: "12px 8px",
                textAlign: "left",
                background:
                  feedback && id === card.answer
                    ? "rgba(44, 122, 59, 0.18)"
                    : undefined,
              }}
            >
              {IALA_MARKS[id]?.shortMeaning ?? id}
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

function EndCard({
  result,
  onBack,
}: {
  result: SessionResult;
  onBack: () => void;
}) {
  const rolling = IALA_IDS
    .map((id) => ({ id, ...statsFor(MODULE, id) }))
    .filter((s) => s.attempts >= 3 && s.hitRate < 1)
    .sort((a, b) => a.hitRate - b.hitRate);
  const weakest = rolling.slice(0, 5);

  return (
    <>
      <h2>Session complete</h2>
      <div className="card">
        <p>
          <strong>{result.correct}</strong> / {result.total} correct
          {result.total > 0 && (
            <> ({Math.round((result.correct / result.total) * 100)}%)</>
          )}
          .
        </p>
      </div>
      {weakest.length > 0 && (
        <div className="card">
          <h3>Weakest marks (rolling)</h3>
          <ul className="small muted">
            {weakest.map((w) => (
              <li key={w.id}>
                {IALA_MARKS[w.id]?.name ?? w.id} —{" "}
                {Math.round(w.hitRate * 100)}% over {w.attempts} attempts
              </li>
            ))}
          </ul>
        </div>
      )}
      <button className="secondary" onClick={onBack}>
        ← Back to Buoyage
      </button>
    </>
  );
}
